



import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchUserFromRedis } from "../HomePage/AuthonticationScreens/Register";
import CustomDialog from "../../CustomDialog";
import { apiRequest } from '../../fetchSetup';
import axios from 'axios';

// ─── tiny reusable row ────────────────────────────────────────────────────────
const Row = ({ label, value, labelStyle = {}, valueStyle = {}, containerStyle = {} }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, ...containerStyle }}>
    <span style={{ fontSize: 13, color: '#6B7280', ...labelStyle }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', ...valueStyle }}>{value}</span>
  </div>
);

const MedicinePaymentScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [selected, setSelected] = useState('online');
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [user, setUser] = useState(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [prescriptionUrl, setPrescriptionUrl] = useState(null);
  const [uploadingPrescription, setUploadingPrescription] = useState(false);
  const [ayujaPricing, setAyujaPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  // ── restore cart data ──────────────────────────────────────────────────────
  const getCartData = () => {
    if (location.state?.cart && location.state.cart.length > 0) return location.state;
    try {
      const savedCart = sessionStorage.getItem('pharmacyCart');
      const savedPatient = sessionStorage.getItem('pharmacyPatientDetails');
      const savedAddress = sessionStorage.getItem('pharmacyDeliveryAddress');
      const savedPrescription = sessionStorage.getItem('pharmacyPrescription');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const patientDetails = savedPatient ? JSON.parse(savedPatient) : null;
        const deliveryAddress = savedAddress ? JSON.parse(savedAddress) : null;
        if (cart.length > 0) {
          const subtotal = cart.reduce((s, i) => s + (i.discounted_price || i.price) * i.quantity, 0);
          const deliveryCharge = Number(process.env.REACT_APP_DELIVERY_CHARGE) || 0;
          return {
            cart,
            orderType: location.state?.orderType || 'prescription',
            totalAmount: subtotal + deliveryCharge,
            patientDetails,
            prescriptionFile: savedPrescription,
            deliveryAddress,
            userEmail: location.state?.userEmail || deliveryAddress?.full_name,
            userPhone: location.state?.userPhone || deliveryAddress?.phone,
            appliedPromo: location.state?.appliedPromo || null,
            subscriptionDiscount: location.state?.subscriptionDiscount || 0,
            subscriptionBenefits: location.state?.subscriptionBenefits || null,
            promoDiscount: location.state?.promoDiscount || 0,
            promoCode: location.state?.promoCode || '',
            ayujaPricing: location.state?.ayujaPricing || null,
          };
        }
      }
    } catch (e) { console.error('Error restoring cart:', e); }
    return location.state || {};
  };

  const cartData = getCartData();
  const {
    cart = [],
    orderType,
    totalAmount,          // ← FINAL payable passed from delivery screen (₹421.87)
    deliveryAddress,
    userEmail,
    userPhone,
    patientDetails,
    prescriptionFile,
    subscriptionDiscount = 0,
    promoDiscount = 0,
    promoCode = '',
    subscriptionBenefits = null,
  } = cartData;

  const finalSubscriptionDiscount = Number(subscriptionDiscount);
  const finalPromoDiscount = Number(promoDiscount) || 0;

  // ── fetch AYUJA pricing (or reuse from delivery screen) ───────────────────
  useEffect(() => {
    if (cartData.ayujaPricing) { setAyujaPricing(cartData.ayujaPricing); return; }
    if (!cart.length) return;
    const fetchPricing = async () => {
      setPricingLoading(true);
      try {
        const res = await apiRequest(process.env.REACT_APP_CALCULATE_ORDER_PRICE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.map(i => ({ medicine_id: i.medicine_id, quantity: i.quantity })),
            subscription_discount: finalSubscriptionDiscount,
            promo_discount: finalPromoDiscount,
          }),
        });
        const data = await res.json();
        if (res.ok) setAyujaPricing(data);
      } catch (e) { console.error('AYUJA pricing error:', e); }
      finally { setPricingLoading(false); }
    };
    fetchPricing();
  }, []);

  // ── prescription upload ───────────────────────────────────────────────────
  const uploadPrescriptionToServer = async (prescriptionData) => {
    try {
      setUploadingPrescription(true);
      const base64Response = await apiRequest(prescriptionData);
      const blob = await base64Response.blob();
      let ext = blob.type.includes('pdf') ? 'pdf' : blob.type.includes('png') ? 'png' : 'jpg';
      const formData = new FormData();
      formData.append('prescription', blob, `prescription.${ext}`);
      formData.append('user_email', userEmail || user?.email || 'guest');
      formData.append('timestamp', Date.now().toString());
      const res = await apiRequest(`${process.env.REACT_APP_PRESCRITON_BASE_API_URL}/prescriptions/upload-temp`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      const result = await res.json();
      if (result.success && result.prescription_url) { setPrescriptionUrl(result.prescription_url); return result.prescription_url; }
      return null;
    } catch (e) { return null; }
    finally { setUploadingPrescription(false); }
  };

  const renameTempPrescription = async (tempUrl, orderId) => {
    try {
      const res = await apiRequest(`${process.env.REACT_APP_PRESCRITON_BASE_API_URL}/prescriptions/rename-temp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_url: tempUrl, order_id: orderId }),
      });
      if (!res.ok) throw new Error('Rename failed');
      return (await res.json()).new_url;
    } catch { return tempUrl; }
  };

  const clearPrescriptionData = () => {
    sessionStorage.removeItem('pharmacyPrescription');
    sessionStorage.removeItem('tempPrescriptionUrl');
    setPrescriptionUrl(null);
  };

  useEffect(() => {
    if (prescriptionFile) uploadPrescriptionToServer(prescriptionFile);
  }, [prescriptionFile, userEmail, user]);

  // ── load razorpay ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.Razorpay) { setRazorpayLoaded(true); return; }
    const s = document.createElement('script');
    s.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => setRazorpayLoaded(true);
    s.onerror = () => { setDialogMessage('Failed to load payment gateway. Please refresh.'); setDialogOpen(true); };
    document.body.appendChild(s);
  }, []);

  // ── load user ─────────────────────────────────────────────────────────────
  const getSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };
  const getDiscount = (result) => {
    const subtotal=cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const discount=subtotal-result.pricing_summary.total_selling_revenue
    return discount;
  };
  
  useEffect(() => {
    const load = async () => {
      const userId = sessionStorage.getItem('userId');
      const token = sessionStorage.getItem('token');
      if (!userId || !token) return;
      // try { setUser(await fetchUserFromRedis(userId, token) || null); }
      try {
        const fetchedUser = await fetchUserFromRedis(userId, token);
        setUser(fetchedUser || null);
        setCurrentUser(fetchedUser || null); 
      }
      catch { setUser(null); }
    };
    load();
  }, []);

  // ── guard ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!cart.length || !orderType || !deliveryAddress) {
      setDialogMessage('Missing required order information');
      setDialogOpen(true);
      navigate(-1);
    }
  }, [cart, orderType, deliveryAddress]);

  // ── create order ──────────────────────────────────────────────────────────
  const createMedicineOrder = async (paymentData) => {
    try {
      setLoading(true);
      const userId = 
      currentUser?.user_id ||         
      user?.user_id ||               
      sessionStorage.getItem('userId') || 
      null;
      const userData = currentUser || { user_id: userId, email: userEmail, phone: userPhone || deliveryAddress.phone };
      if (!userData.email) throw new Error('User email is required');
      const items = cart.map(i => ({ medicine_id: i.medicine_id, quantity: i.quantity }));
      const finalDiscount = finalSubscriptionDiscount + finalPromoDiscount;

      const orderPayload = {
        user_id: userData.user_id, user_email: userData.email,
        user_phone: deliveryAddress.phone, order_type: orderType, items,
        patient_details: patientDetails || {},
        delivery_address: {
          full_name: deliveryAddress.full_name, phone: deliveryAddress.phone,
          community_name: deliveryAddress.community_name, address_line1: deliveryAddress.address_line1,
          address_line2: deliveryAddress.address_line2 || '', landmark: deliveryAddress.landmark || '',
          city: deliveryAddress.city, state: deliveryAddress.state, pincode: deliveryAddress.pincode,
          address_type: deliveryAddress.address_type || 'home',
          delivery_time_option: deliveryAddress.delivery_time_option || '',
          delivery_time: deliveryAddress.delivery_time || '',
        },
        payment: {
          payment_id: paymentData.payment_id, order_id: paymentData.order_id || null,
          method: paymentData.method, status: paymentData.status,
          amount: paymentData.totalAmount, gateway: paymentData.gateway || null,
          currency: paymentData.currency || 'INR', transaction_history: paymentData.transaction_history || [],
        },
        totalAmount,
        paymentDetails: {
          payment_id: paymentData.payment_id, order_id: paymentData.order_id,
          method: paymentData.method, status: paymentData.status,
          amount: paymentData.totalAmount, gateway: paymentData.gateway, currency: paymentData.currency,
        },
        notes: `Order from web app - ${orderType}`,
        order_from:`Order from Resident web app - ${orderType}`,
        subscription_discount: finalSubscriptionDiscount,
        promo_discount: finalPromoDiscount,
        promo_code: promoCode || '',
        discount: finalDiscount,
      };

      if (prescriptionUrl) { orderPayload.prescription_url = prescriptionUrl; orderPayload.prescription_verified = false; }
      else if (prescriptionFile) { orderPayload.notes += ' - Prescription upload pending'; }

      const response = await apiRequest(process.env.REACT_APP_CREATE_MEDICINE_ORDER, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload),
      });
      let result;
      try { result = JSON.parse(await response.text()); } catch { throw new Error('Invalid server response'); }
      if (!response.ok) throw new Error(result.error || `Server error: ${response.status}`);
      if (prescriptionUrl?.includes('temp_')) await renameTempPrescription(prescriptionUrl, result.order_id);
      sessionStorage.removeItem('pharmacyCart');
      sessionStorage.removeItem('pharmacyPatientDetails');
      clearPrescriptionData();
      navigate('/medicine-order-success', {
        replace: true,
        state: { orderId: result.order_id, totalAmount, paymentMethod: paymentData.method, paymentStatus: result.payment_status, deliveryAddress, hasPrescription: !!prescriptionUrl },
      });
      const posOrderData={ 
        service_type:"medicine",
          order_id: result.order_id,
          items: cart,
          patient_details: patientDetails,
          subtotal: getSubtotal(),
          discount: getDiscount(result),
          total_amount: totalAmount,
          payment: { method: paymentData.method },
          created_at: new Date().toISOString(),
          batchInfo:result.stock_deduction 
        }
          try {
            const invoice_response = await axios.post(
              `${process.env.REACT_APP_API_INVOICE}/invoice/pos_invoice`,
              posOrderData,
            );
      
          const invoice = invoice_response.data.invoice_details;
          if (invoice.storage_type === "s3") {
            // Open CloudFront URL
            window.open(invoice.cloudfront_url, "_blank");
          } else if (invoice.storage_type === "local") {
            try {
            const token = sessionStorage.getItem("token");

            const response = await axios.get(
              `${process.env.REACT_APP_API_INVOICE}/invoice/download/${posOrderData.order_id}`,
              {
                responseType: "blob",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            window.open(url, "_blank");

          } catch (error) {
            console.error("Invoice download failed:", error);
          }
          }
          } catch (error) {
            console.error("Invoice generation failed:", error);
          }

      return true;
    } catch (error) {
      const msg = paymentData.status === 'success'
        ? 'Payment was successful but order creation failed. Contact support with payment ID: ' + paymentData.payment_id
        : error.message || 'Failed to create order. Please contact support.';
      setDialogMessage(msg); setDialogOpen(true);
      return false;
    } finally { setLoading(false); }
  };

  const handleOfflinePayment = async () => {
    await createMedicineOrder({
      payment_id: `offline_${Date.now()}`, order_id: null, method: 'offline',
      status: 'pending', amount: totalAmount, gateway: null, currency: 'INR',
      transaction_history: [{ timestamp: new Date().toISOString(), method: 'offline', status: 'pending' }],
    });
  };

  const handleOnlinePayment = async () => {
    if (!razorpayLoaded || !window.Razorpay) { setDialogMessage('Payment gateway is loading. Please try again.'); setDialogOpen(true); return; }
    if (!currentUser && !userEmail) { setDialogMessage('User information is missing'); setDialogOpen(true); return; }
    const userData = currentUser || { email: userEmail };
    try {
      setLoading(true);
      const orderRes = await apiRequest(process.env.REACT_APP_CREATE_RAZORPAY_ORDER, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: totalAmount, currency: 'INR', receipt: `medicine_${Date.now()}`, notes: { order_type: orderType, customer_email: userData.email } }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData.error || 'Failed to create payment order');
      setLoading(false);
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_RLPNXraO5Rywls',
        amount: orderData.totalAmount, currency: orderData.currency,
        name: 'Ayuja Medicines', description: `Medicine Order - ${orderType}`,
        order_id: orderData.id,
        prefill: { name: deliveryAddress.full_name, email: userData.email, contact: deliveryAddress.phone },
        theme: { color: '#006D77' },
        handler: async (paymentSuccess) => {
          setLoading(true);
          try {
            const verifyRes = await apiRequest(process.env.REACT_APP_PROCESS_PAYEMENT, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ razorpay_payment_id: paymentSuccess.razorpay_payment_id, razorpay_order_id: paymentSuccess.razorpay_order_id, razorpay_signature: paymentSuccess.razorpay_signature, method: 'online', amount: orderData.totalAmount }),
            });
            const paymentResult = await verifyRes.json();
            if (paymentResult.status !== 'success') throw new Error('Payment verification failed');
            await createMedicineOrder({
              payment_id: paymentSuccess.razorpay_payment_id, order_id: paymentSuccess.razorpay_order_id,
              method: 'online', status: 'success', amount: orderData.totalAmount / 100, gateway: 'razorpay', currency: 'INR',
              transaction_history: [{ timestamp: new Date().toISOString(), method: 'online', gateway: 'razorpay', payment_id: paymentSuccess.razorpay_payment_id, order_id: paymentSuccess.razorpay_order_id, status: 'success' }],
            });
          } catch (error) {
            setLoading(false);
            setDialogMessage('Payment successful but order creation failed. Contact support with payment ID: ' + paymentSuccess.razorpay_payment_id);
            setDialogOpen(true);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            if (!window.confirm('Transaction unsuccessful. Retry?')) setSelected('offline');
          },
        },
      });
      rzp.on('payment.failed', (r) => {
        setLoading(false);
        if (window.confirm(`Payment Failed: ${r.error.description || r.error.message}. Try again?`)) handleOnlinePayment();
      });
      rzp.open();
    } catch (error) {
      setDialogMessage(error.message || 'Failed to initialize payment.'); setDialogOpen(true); setLoading(false);
    }
  };

  const handleProceed = () => selected === 'offline' ? handleOfflinePayment() : handleOnlinePayment();

  if (!cart.length) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <div style={{ width: 40, height: 40, border: '3px solid #006D77', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  // ── computed totals — mirrors delivery screen exactly ─────────────────────
  const mrpTotal      = cart.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);
  const sellingTotal  = cart.reduce((s, i) => s + (i.discounted_price || i.price || 0) * i.quantity, 0);
  const ayujaSavings  = mrpTotal - sellingTotal;
  const ayujaSubtotal = sellingTotal;
  const ayujaDiscPct  = mrpTotal > 0 ? (ayujaSavings / mrpTotal * 100).toFixed(2) : '0.00';
  // totalAmount (from delivery screen) = ayujaSubtotal - subscriptionDiscount - promoDiscount ← exact match

  return (
    <div style={{ backgroundColor: '#F0F4F8', minHeight: '100vh', paddingBottom: 100, fontFamily: "'Inter', sans-serif" }}>

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'linear-gradient(135deg, #006D77 0%, #004E5A 100%)',
        boxShadow: '0 2px 12px rgba(0,109,119,0.3)',
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', height: 64 }}>
          <button onClick={() => navigate(-1)} disabled={loading} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, width: 38, height: 38,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginRight: 16,
          }}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>Secure Checkout</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>Complete your medicine order</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '4px 12px' }}>
            <svg width="14" height="14" fill="none" stroke="#4ADE80" strokeWidth="2.5" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" /></svg>
            <span style={{ fontSize: 12, color: '#4ADE80', fontWeight: 600 }}>256-bit SSL Encrypted</span>
          </div>
        </div>
      </div>

      {/* ── BODY ────────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px', display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ════════════ LEFT COLUMN ════════════ */}
        <div style={{ flex: '1 1 440px', minWidth: 300, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Payment Method Card */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Choose Payment Method</div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>Select how you'd like to pay</div>
            </div>

            {/* Online */}
            <div
              onClick={() => !loading && setSelected('online')}
              style={{
                margin: '16px 16px 8px', borderRadius: 14, cursor: 'pointer',
                border: selected === 'online' ? '2px solid #006D77' : '1.5px solid #E2E8F0',
                background: selected === 'online' ? 'linear-gradient(135deg,#f0fdf4,#e8faf4)' : '#FAFAFA',
                transition: 'all 0.2s', padding: '16px 18px',
                boxShadow: selected === 'online' ? '0 4px 16px rgba(0,109,119,0.12)' : 'none',
                position: 'relative',
              }}
            >
              {selected === 'online' && (
                <div style={{ position: 'absolute', top: -11, right: 14, background: 'linear-gradient(135deg,#006D77,#00A896)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, letterSpacing: 0.5 }}>
                  ⭐ RECOMMENDED
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg,#006D77,#00A896)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💳</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
                      Pay Online
                      <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>INSTANT</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>UPI · Card · Net Banking · Wallets</div>
                  </div>
                </div>
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: selected === 'online' ? '6px solid #006D77' : '2px solid #CBD5E1', background: '#fff', transition: 'all 0.2s', flexShrink: 0 }} />
              </div>
            </div>

            {/* Cash on Delivery */}
            <div
              onClick={() => !loading && setSelected('offline')}
              style={{
                margin: '0 16px 16px', borderRadius: 14, cursor: 'pointer',
                border: selected === 'offline' ? '2px solid #006D77' : '1.5px solid #E2E8F0',
                background: selected === 'offline' ? 'linear-gradient(135deg,#f0fdf4,#e8faf4)' : '#FAFAFA',
                transition: 'all 0.2s', padding: '16px 18px',
                boxShadow: selected === 'offline' ? '0 4px 16px rgba(0,109,119,0.12)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg,#F59E0B,#D97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💵</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
                      Cash on Delivery
                      <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>PAY LATER</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>Pay when you receive at door</div>
                  </div>
                </div>
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: selected === 'offline' ? '6px solid #006D77' : '2px solid #CBD5E1', background: '#fff', transition: 'all 0.2s', flexShrink: 0 }} />
              </div>
            </div>
          </div>

          {/* Delivery Address Card */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" fill="none" stroke="#3B82F6" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Delivery Address</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>Where should we deliver?</div>
              </div>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#006D77,#00A896)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
                  {(deliveryAddress?.full_name || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>{deliveryAddress?.full_name || 'N/A'}</div>
                  <div style={{ fontSize: 12, color: '#006D77', fontWeight: 600 }}>{deliveryAddress?.phone || userPhone || 'N/A'}</div>
                </div>
              </div>
              <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                <strong>{deliveryAddress?.community_name}</strong><br />
                Block: {deliveryAddress?.address_line1}
                {deliveryAddress?.address_line2 && `, Flat: ${deliveryAddress.address_line2}`}
                {deliveryAddress?.city && <><br />{deliveryAddress.city}{deliveryAddress.state ? `, ${deliveryAddress.state}` : ''}{deliveryAddress.pincode ? ` - ${deliveryAddress.pincode}` : ''}</>}
              </div>
              {deliveryAddress?.delivery_time && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, background: '#EFF6FF', borderRadius: 8, padding: '8px 12px' }}>
                  <svg width="14" height="14" fill="none" stroke="#3B82F6" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" /></svg>
                  <span style={{ fontSize: 12, color: '#3B82F6', fontWeight: 600 }}>{deliveryAddress.delivery_time}</span>
                </div>
              )}
            </div>
          </div>

          {/* Patient Details Card */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" fill="none" stroke="#A855F7" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>Patient Details</div>
            </div>
            <div style={{ padding: '16px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['Name', patientDetails?.name], ['Age / DOB', patientDetails?.dob], ['Gender', patientDetails?.gender], ['Phone', patientDetails?.phone]].map(([label, value]) => (
                <div key={label} style={{ background: '#F8FAFC', borderRadius: 10, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{value || 'N/A'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ════════════ RIGHT COLUMN — ORDER SUMMARY ════════════ */}
        <div style={{ flex: '0 0 370px', minWidth: 300 }}>
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>

            {/* Summary header */}
            <div style={{ background: 'linear-gradient(135deg,#006D77 0%,#004E5A 100%)', padding: '18px 22px' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Order Summary</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{cart.length} item{cart.length !== 1 ? 's' : ''} · All prices incl. GST</div>
            </div>

            <div style={{ padding: '16px 18px', maxHeight: '70vh', overflowY: 'auto' }}>

              {/* ── Item-wise breakdown ── */}
              {cart.map((item, idx) => {
                const mrp = item.price || 0;
                const sp = item.discounted_price || mrp;
                const itemTotal = sp * item.quantity;
                const discPct = mrp > sp ? ((mrp - sp) / mrp * 100) : 0;
                const savings = (mrp - sp) * item.quantity;
                let gstRate = item.igst > 0 ? item.igst : (item.cgst || 0) + (item.sgst || 0);
                const baseExcl = sp / (1 + gstRate / 100);
                const gstAmt = sp - baseExcl;
                const totalBase = baseExcl * item.quantity;
                const totalGst = gstAmt * item.quantity;
                const bp = item.base_price || 0;
                const maxDiscPct = bp > 0 && mrp > bp ? ((mrp - bp) / mrp * 100) : 0;

                return (
                  <div key={idx} style={{ border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden', marginBottom: 10 }}>
                    {/* item header */}
                    <div style={{ background: '#F8FAFC', padding: '10px 13px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #E2E8F0' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>Qty: {item.quantity}{item.pack_size ? ` · Pack: ${item.pack_size}` : ''}</div>
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: 8, flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#15803D' }}>₹{itemTotal.toFixed(2)}</div>
                        {discPct > 0 && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#16A34A', borderRadius: 6, padding: '1px 6px', marginTop: 2, display: 'inline-block' }}>{discPct.toFixed(1)}% OFF</div>
                        )}
                      </div>
                    </div>
                    {/* price detail rows */}
                    <div style={{ padding: '10px 13px', background: '#fff' }}>
                      <Row label="MRP (per unit)" value={<s style={{ color: '#CBD5E1' }}>₹{mrp.toFixed(2)}</s>} />
                      <Row label="Selling Price (per unit, incl. GST)" value={`₹${sp.toFixed(2)}`} valueStyle={{ color: '#0F172A', fontWeight: 700 }} />
                      <Row label={`${item.quantity} × ₹${sp.toFixed(2)}`} value={`₹${itemTotal.toFixed(2)}`} valueStyle={{ fontWeight: 700 }} />
                      {gstRate > 0 && (
                        <>
                          <div style={{ height: 1, background: '#F1F5F9', margin: '7px 0' }} />
                          <Row label="Base Price excl. GST" value={`₹${totalBase.toFixed(2)}`} labelStyle={{ color: '#CBD5E1', fontSize: 11 }} valueStyle={{ color: '#CBD5E1', fontSize: 11, fontWeight: 500 }} />
                          <Row
                            label={`GST (${gstRate}%${item.cgst > 0 && item.sgst > 0 ? ` = CGST ${item.cgst}% + SGST ${item.sgst}%` : item.igst > 0 ? ' IGST' : ''})`}
                            value={`₹${totalGst.toFixed(2)}`}
                            labelStyle={{ color: '#CBD5E1', fontSize: 11 }}
                            valueStyle={{ color: '#CBD5E1', fontSize: 11, fontWeight: 500 }}
                          />
                        </>
                      )}
                      {savings > 0 && (
                        <>
                          <div style={{ height: 1, background: '#F1F5F9', margin: '7px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>🎉 You save on this item</span>
                            <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 700 }}>₹{savings.toFixed(2)} ({discPct.toFixed(1)}% OFF)</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* ── Total savings banner ── */}
              {ayujaSavings > 0 && (
                <div style={{ background: 'linear-gradient(135deg,#F0FDF4,#DCFCE7)', border: '1.5px solid #86EFAC', borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#15803D', fontWeight: 700 }}>🎉 Total Savings on this order</div>
                    <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2 }}>MRP Total: ₹{mrpTotal.toFixed(2)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#15803D' }}>₹{ayujaSavings.toFixed(2)}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#16A34A', borderRadius: 6, padding: '1px 8px', display: 'inline-block', marginTop: 2 }}>{ayujaDiscPct}% OFF</div>
                  </div>
                </div>
              )}

              {/* ── Bill totals — EXACT match to delivery screen ── */}
              <div style={{ background: '#F8FAFC', borderRadius: 14, padding: '14px 16px' }}>

                {/* MRP Total */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#94A3B8' }}>MRP Total</span>
                  <span style={{ fontSize: 13, color: '#CBD5E1', textDecoration: 'line-through', fontWeight: 600 }}>₹{mrpTotal.toFixed(2)}</span>
                </div>

                {/* AYUJA Savings */}
                {ayujaSavings > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, background: '#F0FDF4', borderRadius: 8, padding: '7px 10px' }}>
                    <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>🎉 AYUJA Savings ({ayujaDiscPct}% OFF)</span>
                    <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 700 }}>-₹{ayujaSavings.toFixed(2)}</span>
                  </div>
                )}

                {/* Subtotal after AYUJA */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Subtotal (incl. GST)</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>₹{ayujaSubtotal.toFixed(2)}</span>
                </div>

                {/* Subscription Discount */}
                {finalSubscriptionDiscount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, background: '#F0FDF4', borderRadius: 8, padding: '7px 10px' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>
                        🎁 Subscription Discount{subscriptionBenefits?.benefits?.medicine_discount ? ` (${subscriptionBenefits.benefits.medicine_discount}%)` : ''}
                      </div>
                      {subscriptionBenefits?.subscription?.name && (
                        <div style={{ fontSize: 11, color: '#059669', marginTop: 1 }}>{subscriptionBenefits.subscription.name}</div>
                      )}
                    </div>
                    <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 700, marginLeft: 8 }}>-₹{finalSubscriptionDiscount.toFixed(2)}</span>
                  </div>
                )}

                {/* Promo Discount */}
                {finalPromoDiscount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, background: '#FEF9C3', borderRadius: 8, padding: '7px 10px' }}>
                    <span style={{ fontSize: 13, color: '#D97706', fontWeight: 600 }}>🏷️ Promo {promoCode ? `(${promoCode})` : ''}</span>
                    <span style={{ fontSize: 13, color: '#D97706', fontWeight: 700 }}>-₹{finalPromoDiscount.toFixed(2)}</span>
                  </div>
                )}

                {/* Delivery */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Delivery Charge</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#16A34A' }}>
                    <s style={{ color: '#CBD5E1', marginRight: 4 }}>₹50</s>FREE
                  </span>
                </div>

                <div style={{ height: 1, background: '#E2E8F0', margin: '10px 0' }} />

                {/* ── TOTAL PAYABLE — exact ₹421.87 ── */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'linear-gradient(135deg,#006D77,#004E5A)',
                  borderRadius: 12, padding: '14px 16px',
                }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>Total Payable</div>
                    {(finalSubscriptionDiscount + finalPromoDiscount + ayujaSavings) > 0 && (
                      <div style={{ fontSize: 10, color: '#4ADE80', marginTop: 3 }}>
                        You save ₹{(finalSubscriptionDiscount + finalPromoDiscount + ayujaSavings).toFixed(2)} total
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#fff' }}>
                    ₹{totalAmount?.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM PROCEED BUTTON ─────────────────────────────────────────── */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#fff', boxShadow: '0 -4px 20px rgba(0,0,0,0.10)', padding: '14px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>{selected === 'online' ? '💳 Pay Online' : '💵 Cash on Delivery'}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0F172A', marginTop: 2 }}>₹{totalAmount?.toFixed(2)}</div>
          </div>
          <button
            onClick={handleProceed}
            disabled={loading}
            style={{
              background: loading ? '#94A3B8' : 'linear-gradient(135deg,#006D77,#00A896)',
              color: '#fff', border: 'none', borderRadius: 14,
              padding: '14px 36px', fontSize: 15, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(0,109,119,0.35)',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8,
              minWidth: 200, justifyContent: 'center',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {loading ? (
              <>
                <div style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                Processing…
              </>
            ) : (
              <>
                {selected === 'online' ? 'Pay Now' : 'Place Order'}
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <CustomDialog open={dialogOpen} message={dialogMessage} onClose={() => setDialogOpen(false)} />
    </div>
  );
};

export default MedicinePaymentScreen;